<template>
  <div class="card telematic-page">
    <h2>Invii telematici</h2>
    <ConfirmDialog group="dogane-invio">
      <template #message="slotProps">
        <div class="confirm-content">
          <i class="pi pi-exclamation-triangle confirm-icon" aria-hidden="true" />
          <div>
            <div class="confirm-type-row">
              <span>Stai per effettuare un invio</span>
              <span class="send-type-badge" :class="`send-type-${slotProps.message.modalita}`">
                {{ slotProps.message.modalitaLabel }}
              </span>
            </div>
            <div class="confirm-file"><i class="pi pi-file" /> {{ slotProps.message.fileName }}</div>
            <div class="confirm-summary">{{ slotProps.message.selection }}</div>
          </div>
        </div>
      </template>
    </ConfirmDialog>
    <Accordion :multiple="true" :activeIndex="[0]" @tab-open="onAccordionOpen">
      <AccordionTab header="Esportazione / Invio">
          <div class="export-fields" :class="{ 'with-send': prepareSend }">
            <div class="choice-card"><span class="choice-title">Materia</span><SelectButton v-model="form.materia" :options="materiaOptions" optionLabel="label" optionValue="value" :allowEmpty="false" /></div>
            <i v-if="form.materia" class="pi pi-arrow-right dependency-arrow" aria-hidden="true" />
            <div v-if="form.materia" class="choice-card"><span class="choice-title">Periodo</span><SelectButton v-model="form.periodo" :options="periodoOptions" optionLabel="label" optionValue="value" :allowEmpty="false" /></div>
            <i v-if="form.periodo" class="pi pi-arrow-right dependency-arrow" aria-hidden="true" />
            <div v-if="form.periodo" class="choice-card interval-fields">
            <span class="choice-title">Intervallo</span>
            <div v-if="form.periodo === 'annuale'" class="field"><label for="annual-year">Anno</label><Calendar id="annual-year" v-model="form.anno" view="year" dateFormat="yy" :showIcon="true" :manualInput="false" :maxDate="maxDate" /></div>
            <template v-else-if="form.periodo === 'semestrale'">
              <div class="field"><label for="semester-year">Anno</label><Calendar id="semester-year" v-model="form.anno" view="year" dateFormat="yy" :showIcon="true" :manualInput="false" :maxDate="maxDate" /></div>
              <div class="field"><label for="semester">Semestre</label><Dropdown id="semester" v-model="form.semestre" :options="semestreOptions" optionLabel="label" optionValue="value" /></div>
            </template>
            <div v-else-if="form.periodo === 'mensile'" class="field"><label for="export-month">Anno e mese</label><Calendar id="export-month" v-model="form.mese" view="month" dateFormat="mm/yy" :showIcon="true" :manualInput="false" :maxDate="maxDate" /></div>
            </div>
            <i v-if="selectionComplete" class="pi pi-arrow-right dependency-arrow" aria-hidden="true" />
            <div v-if="selectionComplete" class="choice-card actions-card">
              <span class="choice-title">Azioni</span>
              <Button label="Esporta XML" icon="pi pi-file" class="p-button-info" :loading="exporting" :disabled="!selectionComplete" @click="exportXml" />
              <Button label="Prepara invio telematico" icon="pi pi-send" class="p-button-warning" :disabled="!selectionComplete || tableExists !== true" @click="prepareSend = true" />
            </div>
            <template v-if="prepareSend">
              <i class="pi pi-arrow-right dependency-arrow" aria-hidden="true" />
              <div class="send-extension">
                <div class="send-heading">
                  <h3>Invio telematico</h3>
                  <p>Il file sarà associato a: <strong>{{ selectionSummary }}</strong>.</p>
                </div>
                <div class="send-flow">
                  <div class="choice-card"><span class="choice-title">Ambiente</span><SelectButton v-model="sendForm.modalita" :options="modalitaOptions" optionLabel="label" optionValue="value" :allowEmpty="false" /></div>
                  <i v-if="sendForm.modalita" class="pi pi-arrow-right dependency-arrow" aria-hidden="true" />
                  <div v-if="sendForm.modalita" class="choice-card file-card"><span class="choice-title">File XML</span><FileUpload ref="xmlUpload" mode="basic" name="file" accept=".xml,text/xml,application/xml" chooseLabel="Carica XML" chooseIcon="pi pi-upload" :customUpload="true" :auto="false" @select="onFileSelect" @clear="clearFile" /><small v-if="fileError" class="file-error">{{ fileError }}</small><div v-else-if="sendForm.file" class="selected-file" :title="sendForm.file.name"><i class="pi pi-file" /><span>{{ sendForm.file.name }}</span></div><Button label="Invia" icon="pi pi-send" :loading="sending" :disabled="!canSend" @click="confirmSend" /></div>
                </div>
                <small v-if="sendNotice" class="notice">{{ sendNotice }}</small>
              </div>
            </template>
          </div>
        <small v-if="exportNotice" class="notice">{{ exportNotice }}</small>
      </AccordionTab>
      <AccordionTab header="Storico invii telematici">
        <InlineMessage v-if="tableExists === false" severity="info">Storico non presente</InlineMessage>
        <template v-else>
          <div v-if="historyLoaded" class="history-toolbar">
            <Button label="Aggiorna storico" icon="pi pi-refresh" class="p-button-outlined" :loading="historyLoading" @click="loadHistory(1)"/>
          </div>
          <div v-if="historyError" class="p-message p-message-error history-message">{{ historyError }}</div>
          <DataTable :value="historyItems" :loading="historyLoading" paginator lazy :rows="10" :totalRecords="historyTotal" responsiveLayout="scroll" @page="loadHistoryPage">
            <Column v-for="column in historyColumns" :key="column.field" :field="column.field" :header="column.header"><template #body="{ data }"><Button v-if="column.field === 'nome_xml' && data.nome_xml" :label="data.nome_xml" icon="pi pi-download" class="p-button-link" @click="downloadHistoryXml(data)" /><template v-else>{{ formatValue(column.field, data[column.field], data) }}</template></template></Column>
            <template #empty>Nessun invio telematico presente.</template>
          </DataTable>
        </template>
      </AccordionTab>
    </Accordion>
  </div>
</template>

<script>
import { computed, getCurrentInstance, onMounted, reactive, ref, watch } from "vue";
import { useConfirm } from "primevue/useconfirm";
import DoganeService from "@/services/ask-pratiche/dogane-service";
import InlineMessage from "primevue/inlinemessage";

export default {
  components: { InlineMessage },
  setup() {
    const self = getCurrentInstance();
    const service = new DoganeService(self.appContext.config.globalProperties.$http, self.appContext.config.globalProperties.$eventBus);
    const confirm = useConfirm();
    const year = new Date().getFullYear();
    const materiaOptions = [{ label: "EE", value: "ee" }, { label: "GAS", value: "gas" }];
    const periodoOptions = [{ label: "Annuale", value: "annuale" }, { label: "Semestrale", value: "semestrale" }, { label: "Mensile", value: "mensile" }];
    const semestreOptions = [{ label: "1° semestre", value: "1" }, { label: "2° semestre", value: "2" }];
    const modalitaOptions = [{ label: "Addestramento", value: "addestramento" }, { label: "Reale", value: "reale" }];
    const form = reactive({ materia: null, periodo: null, anno: null, semestre: null, mese: null });
    const sendForm = reactive({ modalita: null, file: null });
    const xmlUpload = ref(null);
    const fileError = ref("");
    const prepareSend = ref(false);
    const exporting = ref(false), sending = ref(false), exportNotice = ref(""), sendNotice = ref("");
    const historyItems = ref([]), historyColumns = ref([]), historyTotal = ref(0), historyLoading = ref(false), historyError = ref(""), historyLoaded = ref(false);
    const tableExists = ref(null);
    let availabilityRequest = null;
    const checkAvailability = () => {
      if (!availabilityRequest) {
        availabilityRequest = service.getDisponibilitaInviiTelematici()
          .then(response => { tableExists.value = response.table_exists === true; })
          .catch(() => { tableExists.value = null; historyError.value = "Impossibile verificare la disponibilità degli invii telematici."; });
      }
      return availabilityRequest;
    };
    const maxDate = new Date(year, 11, 31);
    const selectionComplete = computed(() => {
      if (!form.materia || !form.periodo) return false;
      if (form.periodo === "annuale") return form.anno instanceof Date && form.anno.getFullYear() <= year;
      return form.periodo === "semestrale" ? form.anno instanceof Date && form.anno.getFullYear() <= year && Boolean(form.semestre) : form.mese instanceof Date && form.mese.getFullYear() <= year;
    });
    const years = computed(() => { const d = form.periodo === "mensile" ? form.mese : form.anno; return d instanceof Date ? String(d.getFullYear()) : ""; });
    const selectionSummary = computed(() => {
      if (!selectionComplete.value) return "Selezione incompleta";
      const materia = form.materia.toUpperCase();
      if (form.periodo === "annuale") {
        return `${materia} → Annuale → ${form.anno.getFullYear()}`;
      }
      if (form.periodo === "semestrale") {
        return `${materia} → Semestrale → ${form.anno.getFullYear()}, ${form.semestre}° semestre`;
      }
      const month = String(form.mese.getMonth() + 1).padStart(2, "0");
      return `${materia} → Mensile → ${month}/${form.mese.getFullYear()}`;
    });
    const canSend = computed(() => selectionComplete.value && Boolean(sendForm.modalita) && Boolean(sendForm.file));
    watch(() => form.materia, () => { form.periodo = null; });
    watch(() => form.periodo, () => { form.anno = null; form.semestre = null; form.mese = null; });
    watch(() => [form.materia, form.periodo, form.anno, form.semestre, form.mese], () => { prepareSend.value = false; sendForm.modalita = null; sendForm.file = null; fileError.value = ""; sendNotice.value = ""; }, { deep: true });
    const download = (blob, name) => { const url = URL.createObjectURL(new Blob([blob], { type: "application/xml" })); const a = document.createElement("a"); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url); };
    const exportXml = async () => {
      exportNotice.value = "";
      if (!selectionComplete.value) return;
      prepareSend.value = false;
      sendForm.file = null;
      if (form.periodo === "mensile") { exportNotice.value = "Export XML mensile predisposto: rotta backend ancora da associare."; return; }
      exporting.value = true;
      try { const params = form.periodo === "annuale" ? { anno_da: form.anno.getFullYear(), mese_da: "01", anno_a: form.anno.getFullYear(), mese_a: "12" } : { anno: form.anno.getFullYear(), semestre: form.semestre }; const blob = await service.getDichiarazioneXml(form.periodo, form.materia, params); download(blob, `Dichiarazione_${form.periodo}_${form.materia.toUpperCase()}_${years.value}.xml`); } finally { exporting.value = false; }
    };
    const onFileSelect = async event => {
      fileError.value = "";
      const file = event.files?.[0];
      const allowedMimeTypes = ["text/xml", "application/xml"];
      const invalidExtension = !file?.name.toLowerCase().endsWith(".xml");
      const invalidMimeType = file?.type && !allowedMimeTypes.includes(file.type);
      const tooLarge = file?.size > 10 * 1024 * 1024;
      let error = "";

      if (!file || invalidExtension || invalidMimeType) error = "Seleziona un file XML valido.";
      else if (tooLarge) error = "Il file XML non può superare 10 MB.";
      else {
        try {
          const documentXml = new DOMParser().parseFromString(await file.text(), "application/xml");
          if (documentXml.querySelector("parsererror")) error = "Il contenuto del file non è un XML valido.";
        } catch (e) {
          error = "Non è stato possibile leggere il file XML.";
        }
      }

      if (error) {
        sendForm.file = null;
        fileError.value = error;
        xmlUpload.value?.clear();
        return;
      }

      sendForm.file = file;
      fileError.value = "";
      xmlUpload.value?.clear();
      sendForm.file = file;
    };
    const clearFile = () => { sendForm.file = null; };
    const sendXml = async () => {
      if (sending.value) return;
      sending.value = true;
      const payload = new FormData();
      payload.append("file", sendForm.file);
      payload.append("materia", form.materia);
      payload.append("tipologia", form.periodo);
      payload.append("modalita", sendForm.modalita);
      if (form.periodo === "annuale") payload.append("anno", form.anno.getFullYear());
      else if (form.periodo === "semestrale") {
        payload.append("anno", form.anno.getFullYear());
        payload.append("semestre", form.semestre);
      } else {
        payload.append("anno", form.mese.getFullYear());
        payload.append("mese", form.mese.getMonth() + 1);
      }
      try {
        await service.creaInvioTelematico(payload);
        await loadHistory(1);
        form.materia = null;
        form.periodo = null;
        form.anno = null;
        form.semestre = null;
        form.mese = null;
        sendForm.modalita = null;
        sendForm.file = null;
        fileError.value = "";
        sendNotice.value = "";
        prepareSend.value = false;
        xmlUpload.value?.clear();
      }
      finally { sending.value = false; }
    };
    const confirmSend = () => confirm.require({
      group: "dogane-invio",
      header: "Conferma invio telematico",
      message: "Conferma invio",
      modalita: sendForm.modalita,
      modalitaLabel: sendForm.modalita === "reale" ? "REALE" : "ADDESTRAMENTO",
      fileName: sendForm.file?.name,
      selection: selectionSummary.value,
      acceptLabel: "Invia",
      rejectLabel: "Annulla",
      accept: sendXml,
    });
    const loadHistory = async (page = 1) => { historyLoading.value = true; historyError.value = ""; try { const response = await service.getInviiTelematici({ page, per_page: 10 }); const paginator = response.data || {}; historyColumns.value = response.columns || []; historyItems.value = paginator.data || response.items || []; historyTotal.value = Number(paginator.total ?? response.total ?? historyItems.value.length); } catch (e) { historyItems.value = []; historyColumns.value = []; historyTotal.value = 0; historyError.value = "Impossibile caricare lo storico degli invii telematici."; } finally { historyLoading.value = false; historyLoaded.value = true; } };
    const loadHistoryPage = event => loadHistory(event.page + 1);
    const downloadHistoryXml = async item => {
      const blob = await service.downloadXmlInvioTelematico(item.id);
      download(blob, item.nome_xml);
    };
    const onAccordionOpen = async event => {
      if (event.index !== 1) return;
      await checkAvailability();
      if (tableExists.value === true && !historyLoaded.value && !historyLoading.value) loadHistory(1);
    };
    const formatValue = (field, value) => {
      if (value === null || value === undefined || value === "") return "-";
      if (field === "data_creazione") return new Intl.DateTimeFormat("it-IT", { dateStyle: "short", timeStyle: "short" }).format(new Date(value));
      if (field === "materia") return String(value).toUpperCase();
      if (field === "modalita") return value === "reale" ? "Reale" : "Addestramento";
      if (field === "mese") return String(value).padStart(2, "0");
      if (field === "semestre") return `${value}°`;
      return String(value);
    };
    onMounted(() => { self.appContext.config.globalProperties.$eventBus.emit("page-change", { target: self.proxy, currentRoute: self.proxy.$router.currentRoute.value }); checkAvailability(); });
    return { materiaOptions, periodoOptions, semestreOptions, modalitaOptions, form, sendForm, xmlUpload, fileError, prepareSend, tableExists, maxDate, selectionComplete, selectionSummary, canSend, exporting, sending, exportNotice, sendNotice, exportXml, onFileSelect, clearFile, confirmSend, historyItems, historyColumns, historyTotal, historyLoading, historyError, historyLoaded, loadHistory, loadHistoryPage, downloadHistoryXml, onAccordionOpen, formatValue };
  },
};
</script>

<style scoped>
.telematic-page > h2 { margin-top: 0; }
.export-fields { display: flex; align-items: center; justify-content: safe center; gap: 1rem; width: 100%; overflow-x: auto; padding: 1.5rem; }
.export-fields.with-send { padding-top: 5rem; padding-bottom: 2rem; }
.export-fields > .choice-card, .export-fields > .dependency-arrow, .export-fields > .send-extension { flex-shrink: 0; }
.choice-card { display: flex; flex-direction: column; align-items: stretch; gap: .9rem; min-height: 13.5rem; padding: 1.25rem; border: 2px solid #b5c7d9; border-radius: 10px; background: var(--surface-card); box-shadow: 0 3px 10px rgba(31, 62, 92, .1); }
.export-fields > .choice-card { width: 14.5rem; }.export-fields > .interval-fields { width: 20rem; }.export-fields > .actions-card { width: 17rem; }
.choice-title { font-size: 1rem; font-weight: 700; text-align: center; }
.choice-card :deep(.p-selectbutton) { display: flex; flex: 1; flex-direction: column; justify-content: center; gap: .65rem; }
.choice-card :deep(.p-selectbutton .p-button) { justify-content: center; width: 100%; border: 1.5px solid #aabfd4; border-radius: 6px !important; }
.choice-card :deep(.p-selectbutton .p-button.p-highlight) { border-color: var(--primary-color); }
.dependency-arrow { color: var(--primary-color); font-size: 1.25rem; }
.interval-fields { justify-content: flex-start; }.interval-fields > .field { width: 100%; }
.actions-card { justify-content: center; }.actions-card :deep(.p-button) { width: 100%; }
.export-actions { display: flex; align-items: center; justify-content: center; gap: 1rem; min-height: 4.5rem; padding: 1rem 1.25rem; border-top: 1px solid var(--surface-border); background: var(--surface-50); }.export-actions small { color: var(--text-color-secondary); }.export-actions :deep(.p-button) { min-width: 14rem; }
.field { display: flex; flex-direction: column; gap: .5rem; margin: 0; }.field-label, .field > label { font-weight: 600; }
.notice { display: block; margin-top: .65rem; color: var(--text-color-secondary); }
.dependency-hint { color: var(--text-color-secondary); text-align: center; }
.send-extension { position: relative; align-self: center; width: 34rem; min-height: 13.5rem; padding-left: 1rem; border-left: 1px solid var(--surface-border); }
.send-flow { display: grid; grid-template-columns: 13rem auto 18rem; align-items: center; gap: 1rem; min-height: 13.5rem; }
.file-card { justify-content: center; }.file-card :deep(.p-button), .file-card :deep(.p-fileupload) { width: 100%; }
.file-error { color: var(--red-600); font-weight: 600; line-height: 1.3; }
.selected-file { display: flex; align-items: flex-start; gap: .45rem; min-width: 0; padding: .5rem .6rem; border-radius: 6px; background: var(--surface-100); color: var(--text-color); font-size: .82rem; line-height: 1.3; }.selected-file .pi { flex: 0 0 auto; margin-top: .1rem; color: var(--primary-color); }.selected-file span { min-width: 0; overflow-wrap: anywhere; }
.send-heading { position: absolute; top: -3.75rem; left: 1rem; white-space: nowrap; }.send-heading h3, .send-heading p { margin: 0; }.send-heading p { margin-top: .25rem; color: var(--text-color-secondary); }
.summary { padding: .5rem .75rem; border-radius: 6px; background: var(--primary-50); color: var(--primary-700); font-weight: 600; }.summary.incomplete { background: var(--surface-100); color: var(--text-color-secondary); }
.send-form { display: grid; grid-template-columns: minmax(15rem, 1fr) minmax(14rem, 1fr) auto; align-items: start; gap: 1.5rem; padding: 1.25rem; border: 1px solid var(--surface-border); border-radius: 8px; background: var(--surface-50); }.history-message { margin-bottom: 1rem; padding: 1rem; }
.history-toolbar { display: flex; justify-content: flex-end; margin-bottom: 1rem; }
.confirm-content { display: flex; align-items: flex-start; gap: 1rem; min-width: 30rem; padding: .5rem 0; }
.confirm-icon { margin-top: .15rem; color: var(--orange-500); font-size: 1.75rem; }
.confirm-type-row { display: flex; align-items: center; flex-wrap: wrap; gap: .65rem; font-size: 1rem; }
.send-type-badge { display: inline-flex; align-items: center; padding: .3rem .65rem; border-radius: 999px; font-size: .78rem; font-weight: 800; letter-spacing: .04em; }
.send-type-addestramento { background: var(--orange-100); color: var(--orange-800); border: 1px solid var(--orange-300); }
.send-type-reale { background: var(--red-100); color: var(--red-800); border: 1px solid var(--red-300); }
.confirm-file { margin-top: 1rem; font-weight: 600; word-break: break-word; }.confirm-file .pi { margin-right: .4rem; color: var(--primary-color); }
.confirm-summary { margin-top: .5rem; color: var(--text-color-secondary); font-weight: 600; }
@media (max-width: 1100px) { .export-fields, .export-fields.with-send { align-items: stretch; flex-direction: column; width: auto; padding: 1.5rem; }.export-fields > .choice-card, .export-fields > .interval-fields, .export-fields > .actions-card, .send-extension { width: 100%; }.send-extension { min-height: auto; padding: 1rem 0 0; border-top: 1px solid var(--surface-border); border-left: 0; }.send-heading { position: static; margin-bottom: .75rem; white-space: normal; }.send-flow { grid-template-columns: 1fr; }.dependency-arrow { align-self: center; transform: rotate(90deg); }.choice-card { min-height: auto; }.choice-card :deep(.p-selectbutton) { flex: initial; } }
@media (max-width: 768px) { .send-heading, .export-actions { align-items: stretch; flex-direction: column; }.send-form { grid-template-columns: 1fr; }.confirm-content { min-width: 0; } }
</style>
